import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { RoutePath } from '@/core/config/routeConfig';
import { useMessageTemplateStore } from '@/entities/MessageTemplate/model/store';
import { messageTemplateSchema, type MessageTemplateFormValues } from '@/entities/MessageTemplate/model/schema';
import { messageTemplateResponseToFormValues } from '@/entities/MessageTemplate/mappers';
import { EntityForm, EntityFormLayout, EntityFormStack } from '@/shared/ui/EntityFormLayout/EntityFormLayout';
import { BackButton } from '@/shared/ui/BackButton/BackButton';
import FormButtons from '@/shared/ui/FormButtons/FormButtons';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { RequestStatus } from '@/shared/model/requestStatus';
import { useMessageTemplateSubmit } from './useMessageTemplateSubmit';

const { TextareaField } = createRHFFields<MessageTemplateFormValues>();

/** Показывает редактирование сообщения и пояснения */
export const MessageTemplateFormPage = () => {
    const { id } = useParams<{ id: string }>();
    const { current, detailsStatus, detailsError, fetchById, reset } = useMessageTemplateStore();
    const navigate = useNavigate();

    const form = useForm<MessageTemplateFormValues>({
        resolver: zodResolver(messageTemplateSchema),
        defaultValues: {
            text: '',
            description: ''
        },
    });

    const { reset: resetForm } = form;
    const { onSubmit, canEdit } = useMessageTemplateSubmit(form, current);

    useEffect(() => {
        if (id) void fetchById(id);

        return reset;
    }, [id, fetchById, reset]);

    useEffect(() => {
        if (current && current.id === id) resetForm(messageTemplateResponseToFormValues(current));
    }, [current, id, resetForm]);

    if (detailsStatus === RequestStatus.Loading) return <RequestLoading message="Загрузка текстовки..." />;
    if (detailsStatus === RequestStatus.Error) {
        return <RequestError
            message={detailsError ?? 'Не удалось загрузить текстовку'}
            onRetry={() => id
                ? fetchById(id)
                : undefined}
        />;
    }
    if (!current || current.id !== id) return <RequestEmpty message="Текстовка не найдена" />;

    const back = current.group?.id
        ? RoutePath.messageTemplateGroup.replace(':group', current.group.id)
        : RoutePath.messageTemplates;

    const parameters = current.parameters?.map((name) => `{${name}}`).join(', ');

    return (
        <EntityFormLayout title="Текстовка приложения">
            <BackButton url={back} />
            <p>{current.group?.name}</p>
            <FormProvider {...form}>
                <EntityForm onSubmit={form.handleSubmit(onSubmit)}>
                    <EntityFormStack>
                        <TextareaField
                            name="text"
                            label="Текст сообщения"
                            rows={8}
                            maxLength={4000}
                            disabled={!canEdit || form.formState.isSubmitting}
                            isRequired
                        />
                        {parameters && (
                            <p>Сохраните параметры в фигурных скобках: {parameters}</p>
                        )}
                        <TextareaField
                            name="description"
                            label="Пояснение — где используется сообщение"
                            rows={3}
                            maxLength={1000}
                            disabled={!canEdit || form.formState.isSubmitting}
                        />
                    </EntityFormStack>
                    {form.formState.errors.root?.server?.message && (
                        <RequestError message={form.formState.errors.root.server.message} />
                    )}
                    {canEdit && (
                        <FormButtons
                            loading={form.formState.isSubmitting}
                            disabled={!form.formState.isDirty}
                            isEditMode
                            navigate={navigate}
                            customBackRoute={back}
                        />
                    )}
                </EntityForm>
            </FormProvider>
        </EntityFormLayout>
    );
};
